import { Badge, Group, Stack, Text } from '@mantine/core';
import { IconPointFilled } from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import type { Rule } from '@billing/validation/rules';
import { MRT_ColumnDef } from 'mantine-react-table';
import { formatDateTime } from 'utils/dateUtils';
import { useCurrentSiteStore } from 'store';

type RuleRow = Rule & {
  hasNewerUnpublishedVersion?: boolean;
};

export const RuleColumns = ({
  onViewVersions,
}: {
  onViewVersions?: () => void;
} = {}) => {
  const { translate } = useTranslation();
  const { timezone } = useCurrentSiteStore();
  const notAvailableLabel = translate('pages:rules.table.na', 'N/A');
  const columns: MRT_ColumnDef<Rule>[] = [
    {
      accessorKey: 'name',
      header: translate('pages:rules.table.headers.name', 'Name'),
      size: 280,
      Cell: ({ cell }) => (
        <Text
          size="sm"
          fw={500}
          lineClamp={1}
          title={(cell.getValue() as string) || notAvailableLabel}
        >
          {(cell.getValue() as string) || notAvailableLabel}
        </Text>
      ),
    },
    {
      accessorKey: 'version',
      header: translate('pages:rules.table.headers.version', 'Version'),
      size: 90,
      Cell: ({ cell }) => (
        <Text size="sm">{(cell.getValue() as number) || 1}</Text>
      ),
    },
    {
      accessorKey: 'status',
      header: translate('pages:rules.table.headers.status', 'Status'),
      size: 120,
      Cell: ({ cell, row }) => {
        const status = (cell.getValue() as string) || 'draft';
        const ruleRow = row.original as RuleRow;
        const isDraft = status === 'draft' && !ruleRow.active;
        const hasNewerUnpublishedVersion = Boolean(
          ruleRow.hasNewerUnpublishedVersion
        );
        const statusLabel = translate(
          `pages:rules.statusValues.${status}`,
          status ? `${status[0].toUpperCase()}${status.slice(1)}` : status
        );
        const color =
          status === 'published'
            ? 'green'
            : status === 'archived'
            ? 'gray'
            : 'yellow';
        return (
          <Stack gap={2}>
            <Badge color={color} variant="light">
              {isDraft ? 'Draft' : statusLabel}
            </Badge>
            <Group gap={6} wrap="nowrap">
              {hasNewerUnpublishedVersion ? (
                <Text
                  component="button"
                  type="button"
                  size="xs"
                  c="yellow.8"
                  fw={600}
                  style={{
                    cursor: onViewVersions ? 'pointer' : 'default',
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    textAlign: 'left',
                  }}
                  onClick={onViewVersions}
                  disabled={!onViewVersions}
                >
                  <Group gap={4} wrap="nowrap" align="center">
                    <IconPointFilled
                      size={10}
                      aria-hidden="true"
                      focusable="false"
                      color="var(--mantine-color-yellow-6)"
                    />
                    <span>New draft available</span>
                  </Group>
                </Text>
              ) : null}
            </Group>
          </Stack>
        );
      },
    },
    {
      accessorKey: 'active',
      header: translate('pages:rules.table.headers.active', 'Active'),
      size: 90,
      Cell: ({ cell }) => (
        <Badge
          color={(cell.getValue() as boolean) ? 'green' : 'gray'}
          variant="light"
        >
          {(cell.getValue() as boolean)
            ? translate('pages:rules.table.activeValues.yes', 'Yes')
            : translate('pages:rules.table.activeValues.no', 'No')}
        </Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: translate('pages:rules.table.headers.createdAt', 'Created At'),
      size: 190,
      Cell: ({ cell }) => (
        <Text size="sm">
          {formatDateTime(cell.getValue() as string, {
            timezone,
            fallback: notAvailableLabel,
          })}
        </Text>
      ),
    },
    {
      accessorKey: 'lastUpdatedAt',
      header: translate('pages:rules.table.headers.updatedAt', 'Updated At'),
      size: 190,
      Cell: ({ cell }) => (
        <Text size="sm">
          {formatDateTime(cell.getValue() as string, {
            timezone,
            fallback: notAvailableLabel,
          })}
        </Text>
      ),
    },
  ];

  return columns;
};
